import type { PayloadAction } from '@reduxjs/toolkit';
import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import i18n from '../../i18n';
import { SAGA_ACTIONS } from '../../sagas/sagaActions';
import type { BillingState } from '../../store/billingSlice';
import { setPlacing } from '../../store/checkoutSlice';
import {
  setPaymentError,
  setPaymentMethod,
  setSelectedMethod,
  setSignature,
} from '../../store/paymentSlice';
import type { Localization } from '../../types/localization';
import type {
  Order,
  OrderAccount,
  OrderFault,
  SelectedMethod,
} from '../../types/payment';
import type { Session } from '../../types/session';
import { isJapan, isUSA } from '../../utils/validators/countries';
import { getPageSignature, getPayPalBaid, initPayPal } from '../requestSagas';
import { extractGatewayMessage } from './gatewayResponse';

export function* watchPaymentMethods(): SagaIterator<void> {
  yield takeEvery(setSelectedMethod, handleMethodChange);
  yield takeEvery(SAGA_ACTIONS.PAYPAL_INIT, handlePayPalInit);
  yield takeEvery(SAGA_ACTIONS.PAYPAL_GET_BAID, handlePayPalBaid);
}

function* handleMethodChange(
  action: PayloadAction<SelectedMethod>,
): SagaIterator<void> {
  yield put(setSignature(null));
  yield put(setPaymentError(null));

  const method = action.payload;
  if (method !== 'CreditCard' && method !== 'CreditCardUS') {
    return;
  }

  const pageId =
    method === 'CreditCardUS'
      ? window.ZUORA_PAYMENT_PAGE_CREDIT_CARD_US_ID
      : window.ZUORA_PAYMENT_PAGE_CREDIT_CARD_ID;

  const signature = yield call(getPageSignature, pageId);
  if (!signature) {
    yield put(setPaymentError(i18n.t('Failed to load the payment form.')));
    return;
  }

  yield put(setSignature(signature));
}

function* handlePayPalInit(): SagaIterator<void> {
  const localization = yield select((state) => state.app.localization);

  yield put(setPaymentError(null));

  const result = yield call(
    initPayPal,
    isUSA(localization?.countryCode ?? ''),
    isJapan(localization?.countryCode ?? ''),
  );
  if (result) {
    window.open(result.uri, '_blank', 'width=600,height=800');
  } else {
    yield put(setPaymentError(i18n.t('PayPal init failed.')));
  }
}

function* handlePayPalBaid(action: {
  type: string;
  token: string;
}): SagaIterator<void> {
  const localization = yield select((state) => state.app.localization);

  yield put(setPlacing(true));

  const result = yield call(
    getPayPalBaid,
    action.token,
    isUSA(localization?.countryCode ?? ''),
    isJapan(localization?.countryCode ?? ''),
  );
  if (result) {
    yield put(
      setPaymentMethod({
        PaypalBaid: result.baid,
        PaypalEmail: result.email,
        Type: 'PayPal',
        PayPalType: 'ExpressCheckout',
      }),
    );
  } else {
    yield put(setPaymentError(i18n.t('PayPal payment failed.')));
    yield put(setPlacing(false));
  }
}

export function getPlaceOrderError(order: Order | OrderFault | null): string {
  const failed = i18n.t('Placing order failed.');
  const message =
    order && 'message' in order ? extractGatewayMessage(order.message) : '';

  return message ? `${failed} ${message}` : failed;
}

export function buildOrderAccount(
  billing: BillingState,
  localization: Localization,
  session: Session | null,
): OrderAccount {
  const { form, countryState, hasBillingInfo } = billing;

  return {
    currency: localization.currencyCode,
    address: form.address,
    city: form.city,
    country: form.country,
    firstName: form.firstName,
    lastName: form.lastName,
    email: session?.Email || form.email,
    zipCode: form.zip,
    state: countryState,
    company: form.company,
    vatId: form.vat,
    IsNewAccount: !hasBillingInfo,
  };
}
